import React, { useState, useEffect } from 'react';
import YangBalance from '../components/YangBalance';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import HomeIcon from '@mui/icons-material/Home';
import StarIcon from '@mui/icons-material/Star';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SchoolIcon from '@mui/icons-material/School';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GroupsIcon from '@mui/icons-material/Groups';
import { useNavigate } from 'react-router-dom';
import ConstructionIcon from '@mui/icons-material/Construction';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getYangBalance, purchaseAndGrantProduct } from '../api/yang';
import { useUser } from '../hooks/useUser';
import { supabase } from '../lib/supabase';

// 카테고리 정의
const categories = [
  { id: 'subscription', label: '정기 구독', icon: <StarIcon /> },
  { id: 'mee', label: 'MEE 응시권', icon: <SchoolIcon /> },
  { id: 'mpt', label: 'MPT 응시권', icon: <PsychologyIcon /> },
  { id: 'study', label: '셀프 스터디', icon: <CompareArrowsIcon /> },
  { id: 'bundle', label: '패키지 할인', icon: <LocalOfferIcon /> }
];

// 정기 구독 요금제
const subscriptionTiers = [
  {
    product_code: 'BLL_MONTHLY',
    title: 'BLL 학습권 (1달)',
    price: 100,
    period: '월',
    description: '1개월 이용권 · 100🐾(냥)',
    features: [
      '모든 학습 자료와 기본 서비스 이용',
      'Community 서비스 이용',
      '오답 노트 무제한 저장',
      '기본 학습 분석 대시보드',
      '신규 콘텐츠 우선 이용권'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained',
    highlight: true
  },
  {
    product_code: 'BLL_6MONTH',
    title: 'BLL 정기권 (6달, 10% 할인)',
    price: 540,
    originalPrice: 600,
    period: '6개월',
    description: '6개월 이용권 · 540🐾(냥) (10% 할인)',
    discount: '10%',
    features: [
      '6개월간 모든 학습 자료와 서비스 이용',
      '매월 1회 MEE/MPT AI 평가 무료',
      '셀프 스터디 1문제 무료',
      '학습권 구매 시 10% 할인',
      '추가 특별 혜택 제공'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained',
    highlight: true
  }
];

// MEE 응시권
const meeTiers = [
  {
    product_code: 'MEE_AI_6',
    title: 'MEE 응시권 (AI 평가, 6문제)',
    price: 100,
    period: '회',
    description: '1회(6문제) · 100🐾(냥)',
    features: [
      '6개 문제 응시 및 제출',
      'AI 평가',
      '자격: BLL 학습권/정기권 보유자',
      '정기권 보유자 1문제 AI 평가 무료',
      '48시간 이내 결과 확인',
      '상세한 AI 평가 리포트 제공'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained'
  },
  {
    product_code: 'MEE_PROFESSOR_6',
    title: 'MEE 응시권 (Professor 평가, 6문제)',
    price: 150,
    period: '회',
    description: '1회(6문제) · 150🐾(냥)',
    features: [
      '6개 문제 응시 및 제출',
      'Professor 평가',
      '자격: BLL 학습권/정기권 보유자',
      '정기권 보유자 1문제 AI 평가 무료',
      '72시간 이내 결과 확인',
      '상세한 피드백 리포트 제공'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained'
  }
];

// MPT 응시권
const mptTiers = [
  {
    product_code: 'MPT_AI_2',
    title: 'MPT 응시권 (AI 평가, 2문제)',
    price: 150,
    period: '회',
    description: '1회(2문제) · 150🐾(냥)',
    features: [
      '2개 문제 응시 및 제출',
      'AI 평가',
      '자격: BLL 학습권/정기권 보유자',
      '정기권 보유자 1문제 AI 평가 무료',
      '48시간 이내 결과 확인',
      '상세한 AI 평가 리포트 제공'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained'
  },
  {
    product_code: 'MPT_PROFESSOR_2',
    title: 'MPT 응시권 (Professor 평가, 2문제)',
    price: 200,
    period: '회',
    description: '1회(2문제) · 200🐾(냥)',
    features: [
      '2개 문제 응시 및 제출',
      'Professor 평가',
      '자격: BLL 학습권/정기권 보유자',
      '정기권 보유자 1문제 AI 평가 무료',
      '72시간 이내 결과 확인',
      '상세한 피드백 리포트 제공'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained'
  }
];

// 셀프 스터디 옵션
const studyTiers = [
  {
    product_code: 'STUDY_MEE_1',
    title: '셀프 MEE 스터디권 (1문제)',
    price: 20,
    period: '회',
    description: '1회(1문제) · 20🐾(냥)',
    features: [
      '1문제 자가 학습',
      '자격: BLL 학습권/정기권 보유자',
      '정기권 보유자 1문제 무료',
      '모범 답안 즉시 확인'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained'
  },
  {
    product_code: 'STUDY_MPT_1',
    title: '셀프 MPT 스터디권 (1문제)',
    price: 30,
    period: '회',
    description: '1회(1문제) · 30🐾(냥)',
    features: [
      '1문제 자가 학습',
      '자격: BLL 학습권/정기권 보유자',
      '정기권 보유자 1문제 무료',
      '모범 답안 즉시 확인'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained'
  }
];

// 패키지 할인 번들
const bundleTiers = [
  {
    product_code: 'BUNDLE_PASS',
    title: 'MEE + MPT 합격 패키지',
    price: 1200,
    originalPrice: 1550,
    discount: '22%',
    description: 'MEE 응시권 3회 (AI 2, Prof 1) + MPT 응시권 3회 (AI 2, Prof 1) + MEE 스터디권 20회 + MPT 스터디권 10회 · 1200🐾(냥)',
    features: [
      '자격: BLL 학습권/정기권 보유자',
      'MEE 응시권 3회 (AI 2, Professor 1)',
      'MPT 응시권 3회 (AI 2, Professor 1)',
      'MEE 스터디권 20회',
      'MPT 스터디권 10회'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained',
    highlight: true
  },
  {
    product_code: 'BUNDLE_PROFESSOR',
    title: 'Professor 완성 패키지',
    price: 1800,
    originalPrice: 2150,
    discount: '16%',
    description: 'MEE 응시권 5회 (AI 3, Prof 2) + MPT 응시권 5회 (AI 3, Prof 2) + MEE 스터디권 20회 + MPT 스터디권 10회 · 1800🐾(냥)',
    features: [
      '자격: BLL 학습권/정기권 보유자',
      'MEE 응시권 5회 (AI 3, Professor 2)',
      'MPT 응시권 5회 (AI 3, Professor 2)',
      'MEE 스터디권 20회',
      'MPT 스터디권 10회'
    ],
    buttonText: '냥으로 결제',
    buttonVariant: 'contained'
  }
];

// 비교 테이블 데이터
const comparisonData = {
  headers: ['평가 유형', '응답 시간', '피드백 상세도', '1:1 질문', '가격'],
  rows: [
    ['AI 평가', '48시간 이내', '높음', '×', '100~150🐾(냥)'],
    ['Professor 평가', '72시간 이내', '매우 높음', '○', '150~200🐾(냥)'],
    ['셀프 스터디', '즉시', '다양한 응시자 답안 비교 분석', '×', '20~30🐾(냥)']
  ]
};

// 맞춤형 추천 시스템
const goals = [
  {
    title: '첫 시험 준비',
    recommended: 'BLL 구독 + 셀프 스터디(~6개월) → MEE+MPT 스터디 패키지 (~6개월)',
    description: '기초 실력 다지기와 다양한 문제 풀이 경험을 위해 BLL 구독과 셀프 스터디를 활용하고, 이후 MEE+MPT 스터디 패키지로 실전 연습을 충분히 진행하세요.'
  },
  {
    title: '실전 감각 향상',
    recommended: 'BLL 구독 + MEE+MPT 스터디 패키지(~6개월) → Professor 완성 패키지 (~3개월)',
    description: '실전 문제 풀이와 다양한 피드백을 통해 실력을 끌어올린 뒤, Professor 완성 패키지로 전문가 평가와 심화 피드백을 받아보세요.'
  },
  {
    title: '최종 합격 준비',
    recommended: 'BLL 구독 + Professor 완성 패키지(~3개월)',
    description: '최종 합격을 목표로, BLL 구독과 Professor 완성 패키지로 집중적인 실전 대비와 전문가 피드백을 받으세요.'
  }
];

// 사용량 기반 할인
const volumeDiscounts = [
  { amount: '3회', discount: '3%' },
  { amount: '5회', discount: '7%' },
  { amount: '10회', discount: '10%' }
];

const getExamTicketCount = async (userId, ticketType) => {
  const { data, error } = await supabase
    .from('user_exam_tickets')
    .select('count')
    .eq('user_id', userId)
    .eq('type', ticketType)
    .single();
  if (error) return 0;
  return data?.count || 0;
};

// 할인 계산 함수
function getBulkDiscountedPrice(unitPrice, count) {
  if (count === 3) return Math.round(unitPrice * count * 0.97);
  if (count === 5) return Math.round(unitPrice * count * 0.93);
  if (count === 10) return Math.round(unitPrice * count * 0.90);
  return unitPrice * count;
}
function getBulkDiscountRate(count) {
  if (count === 3) return 3;
  if (count === 5) return 7;
  if (count === 10) return 10;
  return 0;
}

const Pricing = () => {
  const [hoveredTier, setHoveredTier] = useState(null);
  const [currentTab, setCurrentTab] = useState('subscription');
  const navigate = useNavigate();
  const { user } = useUser();
  const [meeAiTicketCount, setMeeAiTicketCount] = useState(0);
  // 결제 확인 Dialog 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [bulkPurchaseInfo, setBulkPurchaseInfo] = useState(null);

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // 현재 카테고리에 맞는 요금제 선택
  const getCurrentTiers = () => {
    switch(currentTab) {
      case 'subscription': return subscriptionTiers;
      case 'mee': return meeTiers;
      case 'mpt': return mptTiers;
      case 'study': return studyTiers;
      case 'bundle': return bundleTiers;
      default: return subscriptionTiers;
    }
  };

  // Dialog 오픈용 핸들러
  const handleOpenDialog = (tier) => {
    setSelectedProduct(tier);
    setDialogOpen(true);
  };
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProduct(null);
  };

  // 실제 결제 로직
  const handlePurchase = async () => {
    if (!selectedProduct?.product_code) {
      alert('상품 코드가 올바르지 않습니다. 관리자에게 문의하세요.');
      return;
    }
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }
    try {
      await purchaseAndGrantProduct(user.id, selectedProduct.product_code);
      alert('구매 및 상품 지급이 완료되었습니다!');
      setDialogOpen(false);
      setSelectedProduct(null);
      // 필요시 잔고/상품 목록 등 UI 갱신
    } catch (e) {
      alert(e.message || '결제 실패');
    }
  };

  // 대량구매 버튼 클릭 핸들러
  const handleOpenBulkDialog = (productCode, productName, evalType, pricePerUnit, count) => {
    const discountRate = getBulkDiscountRate(count);
    const totalPrice = getBulkDiscountedPrice(pricePerUnit, count);
    setBulkPurchaseInfo({
      productCode,
      productName,
      evalType,
      pricePerUnit,
      count,
      discountRate,
      totalPrice,
      originalTotal: pricePerUnit * count
    });
    setDialogOpen(true);
  };

  // Dialog에서 결제 버튼 클릭 시
  const handleConfirmBulkPurchase = async () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }
    try {
      for (let i = 0; i < bulkPurchaseInfo.count; i++) {
        await purchaseAndGrantProduct(user.id, bulkPurchaseInfo.productCode);
      }
      alert(`${bulkPurchaseInfo.count}회 대량 구매가 완료되었습니다!`);
      setDialogOpen(false);
      setBulkPurchaseInfo(null);
    } catch (e) {
      alert(e.message || '결제 실패');
    }
  };

  useEffect(() => {
    if (user) {
      getExamTicketCount(user.id, 'MEE_AI_6').then(setMeeAiTicketCount);
    }
  }, [user]);

  // 모든 상품에 product_code가 반드시 존재해야 함
  [...subscriptionTiers, ...meeTiers, ...mptTiers, ...studyTiers, ...bundleTiers].forEach(tier => {
    if (!tier.product_code) {
      console.warn('product_code 누락:', tier.title);
    }
  });

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        pt: 8,
        pb: 6,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
  <IconButton
    onClick={handleHomeClick}
    sx={{
      mr: 2,
      color: 'primary.main',
      '&:hover': {
        bgcolor: 'primary.light',
        color: 'primary.contrastText',
      },
    }}
  >
    <HomeIcon />
  </IconButton>
  <Typography
    component="h1"
    variant="h2"
    align="left"
    color="text.primary"
    sx={{
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700
    }}
  >
    요금제
  </Typography>
  {/* 🐾 내 잔고 표시 (상단 고정) */}
  <Box sx={{ flex: 1 }} />
  <YangBalance />
</Box>
        <Typography
          variant="h5"
          align="center"
          color="text.secondary"
          component="p"
          sx={{ mb: 4 }}
        >
          나에게 맞는 플랜을 선택하세요
        </Typography>

        {/* 카테고리 탭 */}
        <Paper sx={{ borderRadius: 2, mb: 6 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2,
              py: 1,
              '& .MuiTab-root': {
                minHeight: 60,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                px: 3
              }
            }}
          >
            {categories.map(category => (
              <Tab 
                key={category.id}
                value={category.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon}
                    <span>{category.label}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>

        {/* 요금제 카드 */}
        <Grid container spacing={4} alignItems="flex-start" sx={{ mb: 8 }}>
          {getCurrentTiers().map((tier) => (
            <Grid
              item
              key={tier.title}
              xs={12}
              sm={12}
              md={currentTab === 'subscription' ? 12 : 6}
            >
              <Card
                onMouseEnter={() => setHoveredTier(tier.title)}
                onMouseLeave={() => setHoveredTier(null)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    borderStyle: 'solid',
                  },
                  ...(hoveredTier === tier.title && {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    transform: 'scale(1.02)',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                    }
                  }),
                  ...(tier.highlight && {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    borderStyle: 'solid',
                  })
                }}
              >
                {tier.highlight && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      zIndex: 1
                    }}
                  >
                    추천
                  </Box>
                )}

                <CardHeader
                  title={tier.title}
                  titleTypographyProps={{
                    align: 'center',
                    fontWeight: 700,
                    fontSize: '1.5rem'
                  }}
                  sx={{
                    bgcolor: tier.highlight || hoveredTier === tier.title ? 'primary.light' : 'grey.50'
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'baseline',
                      mb: 2,
                    }}
                  >
                    {tier.originalPrice && (
                      <Typography
                        component="span"
                        variant="h6"
                        color="text.secondary"
                        sx={{ 
                          textDecoration: 'line-through',
                          mr: 1,
                          fontWeight: 400
                        }}
                      >
                        <span style={{fontSize: '1.1rem', color: '#888', textDecoration: 'line-through', marginRight: 8}}>
                          {tier.originalPrice}<span style={{fontSize: '1.5rem', verticalAlign: 'middle', margin: '0 4px'}}>🐾</span>(냥)
                        </span>
                      </Typography>
                    )}
                    <Typography
                      component="h2"
                      variant="h3"
                      color="text.primary"
                      sx={{ fontWeight: 700 }}
                    >
                      {tier.price}
                      <span style={{fontSize: '1.5rem', verticalAlign: 'middle', margin: '0 4px'}}>🐾</span>(냥)
                    </Typography>
                    {tier.period && (
                      <Typography variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                        /{tier.period}
                      </Typography>
                    )}
                  </Box>

                  {tier.discount && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Chip 
                        label={`${tier.discount} 할인`} 
                        color="error" 
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  )}

                  <Typography
                    variant="subtitle1"
                    align="center"
                    sx={{ fontStyle: 'italic', mb: 3 }}
                  >
                    {tier.description}
                  </Typography>
                  <List>
                    {tier.features.map((feature) => (
                      <ListItem key={feature} sx={{ py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon sx={{ color: 'primary.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 4 }}>
                  <Button
                    fullWidth
                    variant={tier.highlight || hoveredTier === tier.title ? 'contained' : tier.buttonVariant}
                    onClick={() => handleOpenDialog(tier)}
                    sx={{
                      maxWidth: 200,
                      height: 48,
                      fontSize: '1rem',
                      fontWeight: 600,
                      transition: 'all 0.2s ease-in-out',
                      ...(hoveredTier === tier.title && {
                        transform: 'scale(1.05)',
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark'
                        }
                      })
                    }}
                  >
                    <span style={{fontSize: '1.3rem', verticalAlign: 'middle', marginRight: 4, color: 'white'}}>🐾</span>
                    {tier.buttonText}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 추가 정보 섹션 */}
        {(currentTab === 'mee' || currentTab === 'mpt') && (
          <>
            {/* 비교 테이블 */}
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              평가 유형 비교
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 6, borderRadius: 2, overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    {comparisonData.headers.map((header, index) => (
                      <TableCell key={index} sx={{ fontWeight: 700, color: 'primary.dark' }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonData.rows.map((row, rowIndex) => (
                    <TableRow 
                      key={rowIndex}
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: 'background.default' },
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* 사용량 기반 할인 */}
            <Paper sx={{ p: 3, mb: 6, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                대량 구매 할인
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                {volumeDiscounts.map((discount, index) => (
                  <Paper 
                    key={index} 
                    elevation={2}
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      flex: 1,
                      borderRadius: 2,
                      bgcolor: 'primary.light',
                      color: 'primary.dark'
                    }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      {discount.amount} 구매
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {discount.discount} 할인
                    </Typography>
                    {/* AI 평가로 구매 버튼 */}
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mt: 2, fontWeight: 700, borderRadius: 2, mb: 1 }}
                      fullWidth
                      onClick={() => {
                        let productCode = '';
                        let productName = '';
                        let pricePerUnit = 0;
                        if (currentTab === 'mee') {
                          productCode = 'MEE_AI_6';
                          productName = 'MEE 응시권 (AI 평가, 6문제)';
                          pricePerUnit = 100;
                        } else if (currentTab === 'mpt') {
                          productCode = 'MPT_AI_2';
                          productName = 'MPT 응시권 (AI 평가, 2문제)';
                          pricePerUnit = 150;
                        } else return;
                        const count = parseInt(discount.amount, 10);
                        handleOpenBulkDialog(productCode, productName, 'AI', pricePerUnit, count);
                      }}
                    >
                      {discount.amount} AI 평가로 구매
                    </Button>
                    {/* Professor 평가로 구매 버튼 */}
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ fontWeight: 700, borderRadius: 2 }}
                      fullWidth
                      onClick={() => {
                        let productCode = '';
                        let productName = '';
                        let pricePerUnit = 0;
                        if (currentTab === 'mee') {
                          productCode = 'MEE_PROFESSOR_6';
                          productName = 'MEE 응시권 (Professor 평가, 6문제)';
                          pricePerUnit = 150;
                        } else if (currentTab === 'mpt') {
                          productCode = 'MPT_PROFESSOR_2';
                          productName = 'MPT 응시권 (Professor 평가, 2문제)';
                          pricePerUnit = 200;
                        } else return;
                        const count = parseInt(discount.amount, 10);
                        handleOpenBulkDialog(productCode, productName, 'Professor', pricePerUnit, count);
                      }}
                    >
                      {discount.amount} Professor 평가로 구매
                    </Button>
                  </Paper>
                ))}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                * 동일 응시권 대량 구매 시 적용되는 할인입니다.
              </Typography>
            </Paper>
          </>
        )}

        {/* 맞춤형 추천 시스템 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            학습 단계별 추천 요금제
          </Typography>
          <Grid container spacing={3}>
            {goals.map((goal, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper 
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    borderRadius: 2,
                    ...(index === 1 && {
                      border: '2px solid',
                      borderColor: 'primary.main'
                    })
                  }}
                >
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    {goal.title}
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    color="primary.main" 
                    fontWeight={600} 
                    sx={{ mb: 2 }}
                  >
                    추천: {goal.recommended}
                  </Typography>
                  <Typography variant="body1">
                    {goal.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* 특별 혜택 및 안내 */}
        <Paper sx={{ p: 4, mb: 8, borderRadius: 2, bgcolor: 'primary.light' }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'primary.dark' }}>
            정기 회원 특별 혜택
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  매월 1회 AI 평가 무료
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  정기 구독자에게 매월 MEE 또는 MPT AI 평가 응시권 1회를 무료로 제공합니다.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  분기별 학습 진단 리포트
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  3개월마다 학습 패턴과 진도를 분석한 맞춤형 학습 진단 리포트를 받아보세요.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  신규 콘텐츠 우선 이용
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  새로운 학습 자료와 시험 세트가 출시될 때 가장 먼저 이용할 수 있습니다.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* FAQ 섹션 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            자주 묻는 질문
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  정기 구독(학습권)은 어떻게 결제하나요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  모든 결제는 냥(🐾) 단위로 이루어집니다. 원하는 학습권을 선택한 후, 보유한 냥으로 결제할 수 있습니다. 잔고가 부족할 경우 냥을 충전하실 수 있습니다.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  1달 BLL 학습권을 6개월치 선납하면 어떤 혜택이 있나요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  6개월 선납 시 매월 1회 AI 평가 무료, 학습권 구매 시 20% 할인, 추가 특별 혜택(favor)이 제공됩니다.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  냥(🐾)은 어떻게 충전하나요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  마이페이지 또는 결제 화면에서 원하는 만큼 냥을 충전할 수 있습니다. 충전된 냥은 학습권, 응시권, 스터디권 등 다양한 서비스 결제에 사용할 수 있습니다.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  냥(🐾)은 환불 또는 현금화가 가능한가요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  냥은 현금으로 환불하거나 현금화할 수 없습니다. (운영정책에 따라 예외가 있을 수 있습니다.)
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  정기 구독(학습권) 환불 정책은 어떻게 되나요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  미사용 학습권은 결제일로부터 7일 이내에 환불이 가능합니다. 6개월 선납권도 동일하게 적용됩니다.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  응시권은 얼마나 오래 사용할 수 있나요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  모든 응시권은 구매일로부터 6개월간 유효합니다. 패키지로 구매한 응시권도 동일한 유효기간이 적용됩니다.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  셀프 스터디에서 다른 응시자 답안은 어떻게 볼 수 있나요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  셀프 스터디 모드에서는 답안 제출 후 다른 응시자들의 답안을 유형별로 분류하여 보여드립니다. 우수 답안, 평균 답안, 다양한 접근 방식의 답안을 비교하며 자신의 답안 작성 능력을 향상시킬 수 있습니다.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Professor 평가는 누가 진행하나요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  현직 로스쿨 교수 및 변호사로 구성된 전문가 평가단이 직접 검토하고 피드백을 제공합니다.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Study권과 평가 응시권의 차이점은 무엇인가요?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Study권은 다른 응시자들의 다양한 답안을 비교 분석하며 자가 학습하는 서비스로, 결과를 즉시 확인할 수 있습니다. 평가 응시권은 AI 또는 전문가가 작성한 답안을 직접 평가하고 맞춤형 피드백을 제공받는 서비스입니다.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* 결제 확인 Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setBulkPurchaseInfo(null);
            setSelectedProduct(null);
          }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: 'background.paper',
              boxShadow: 8,
              minWidth: 340,
              p: 1.5,
            }
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: 'primary.light',
              color: 'primary.main',
              fontWeight: 700,
              textAlign: 'center',
              fontSize: '1.25rem',
              letterSpacing: 1,
              borderRadius: 2,
              mb: 1
            }}
          >
            결제 확인
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
            <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary', mb: 2 }}>
              {bulkPurchaseInfo ? (
                <>
                  <span style={{ fontWeight: 700, color: '#1976d2' }}>
                    {bulkPurchaseInfo.productName} {bulkPurchaseInfo.count}회 대량 구매
                  </span>
                  <br />
                  <span style={{ fontWeight: 500, color: '#1976d2', fontSize: '1.2rem' }}>
                    🐾 {bulkPurchaseInfo.totalPrice}냥
                  </span>
                  <br />
                  <span style={{ color: '#666', fontSize: '0.98rem' }}>
                    {bulkPurchaseInfo.productName} {bulkPurchaseInfo.count}회를 구매하고 {bulkPurchaseInfo.originalTotal}냥이 아닌 {bulkPurchaseInfo.discountRate}% 할인된 {bulkPurchaseInfo.totalPrice}냥을 차감합니다.
                  </span>
                </>
              ) : selectedProduct ? (
                <>
                  <span style={{ fontWeight: 700, color: '#1976d2' }}>{selectedProduct.title}</span> <br />
                  <span style={{ fontWeight: 500, color: '#1976d2', fontSize: '1.2rem' }}>
                    🐾 {selectedProduct.price}냥
                  </span>
                  <br />
                  <span style={{ color: '#666', fontSize: '0.98rem' }}>구매를 진행하시겠습니까?</span>
                </>
              ) : null}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button
              onClick={() => {
                setDialogOpen(false);
                setBulkPurchaseInfo(null);
                setSelectedProduct(null);
              }}
              color="inherit"
              variant="outlined"
              sx={{ minWidth: 90, borderRadius: 2 }}
            >
              취소
            </Button>
            <Button
              onClick={bulkPurchaseInfo ? handleConfirmBulkPurchase : handlePurchase}
              color="primary"
              variant="contained"
              sx={{ minWidth: 90, borderRadius: 2, fontWeight: 700 }}
            >
              결제
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
};

export default Pricing; 